import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { merge } from "lodash";
import type { RootState } from "@/store/store";
import { reusableFetch } from "@/utils/useFetch";
import { APPLICATION_FILTER_OPTIONS } from "@/constants";

export const getQuoteList = createAsyncThunk("Quote/getQuoteList", async (data, { rejectWithValue }) => {
  const quoteApplications = await reusableFetch(`application/all`, "POST", data, rejectWithValue);
  return quoteApplications;
});

export const sendQuoteToEmail = createAsyncThunk(
  "Quote/sendQuoteToEmail",
  async (data: { applicationId: string }, { rejectWithValue }) => {
    const quote = await reusableFetch(`quote/send-quote-to-email`, "POST", data, rejectWithValue);
    return quote;
  },
);

interface QuoteFilterDate {
  startDate: string | null;
  endDate: string | null;
}

interface QuoteSlice {
  gettingAllQuotes: boolean;
  sendingQuoteLoader: boolean;
  statusFilter: typeof APPLICATION_FILTER_OPTIONS[keyof typeof APPLICATION_FILTER_OPTIONS];
  applications: Record<string, unknown>;
  quotes: Record<string, unknown>;
  filterDate: QuoteFilterDate;
}

const initialState: QuoteSlice = {
  gettingAllQuotes: false,
  sendingQuoteLoader: false,
  statusFilter: APPLICATION_FILTER_OPTIONS.ALL,
  applications: {},
  quotes: {},
  filterDate: {
    startDate: null,
    endDate: null,
  },
};

export const quoteSlice = createSlice({
  name: "Quote",
  initialState,
  reducers: {
    saveValue: (state, action) => merge(state, action.payload),
    resetState: () => initialState,
    setQuoteFilterDate: (state, action) => {
      state.filterDate = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getQuoteList.fulfilled, (state, { payload }) => {
        state.gettingAllQuotes = false;
        // @ts-expect-error: ignoring as this is existing code
        state.quotes = payload?.data?.data;
      })
      .addCase(getQuoteList.pending, (state) => {
        state.gettingAllQuotes = true;
      })
      .addCase(getQuoteList.rejected, (state) => {
        state.gettingAllQuotes = false;
      })
      .addCase(sendQuoteToEmail.fulfilled, (state) => {
        state.sendingQuoteLoader = false;
      })
      .addCase(sendQuoteToEmail.pending, (state) => {
        state.sendingQuoteLoader = true;
      })
      .addCase(sendQuoteToEmail.rejected, (state) => {
        state.sendingQuoteLoader = false;
      });
  },
});

export const { saveValue, resetState, setQuoteFilterDate } = quoteSlice.actions;

export const quoteSelector = (state: RootState) => state.Quotes;
export const applicationsSelector = (state: RootState) => state.Quotes.applications;
export const quotesSelector = (state: RootState) => state.Quotes.quotes;
